import { FC } from "react";
import { Box, Typography, Modal, IconButton } from "@mui/material";
import { X, AlertTriangle, Pencil, Trash2 } from "lucide-react";

import { CustomButton } from "../../atoms";
import { Colors } from "../../../configs";

interface DiscardConfirmationModalProps {
  open: boolean;
  title?: string;
  message?: string;
  contextLabel?: string;
  keepEditingText?: string;
  discardText?: string;
  onKeepEditing: () => void;
  onDiscard: () => void;
}

const DiscardConfirmationModal: FC<DiscardConfirmationModalProps> = ({
  open,
  title = "Unsaved Changes",
  message = "You have unsaved changes. Are you sure you want to close without saving?",
  contextLabel = "Unsaved Changes",
  keepEditingText = "Keep Editing",
  discardText = "Discard Changes",
  onKeepEditing,
  onDiscard,
}) => {
  return (
    <Modal
      open={open}
      onClose={onKeepEditing}
      disableScrollLock
      disableRestoreFocus
      sx={{ zIndex: 1400 }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.55)",
            backdropFilter: "blur(0.2rem)",
            WebkitBackdropFilter: "blur(0.2rem)",
          },
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "32.5rem",
          bgcolor: Colors.WHITE,
          borderRadius: "1.75rem",
          border: `1px solid ${Colors.ACCENT_GRAY}`,
          boxShadow: "0 1.5rem 4.375rem rgba(0, 0, 0, 0.14)",
          overflow: "hidden",
          outline: "none",
        }}
      >
        {/* Top accent bar */}
        <Box
          sx={{
            height: "0.5rem",
            width: "100%",
            background: Colors.SECONDARY,
          }}
        />

        {/* Content */}
        <Box sx={{ px: "2rem", pb: "2rem", pt: "1.75rem", position: "relative" }}>
          {/* Close button */}
          <IconButton
            aria-label="close"
            onClick={onKeepEditing}
            sx={{
              position: "absolute",
              top: "1rem",
              right: "1rem",
              width: "2.5rem",
              height: "2.5rem",
              border: `1px solid ${Colors.ACCENT_GRAY}`,
              bgcolor: Colors.WHITE,
              color: Colors.MAIN_GRAY,
              "&:hover": { bgcolor: Colors.BACKSHADE_GRAY },
            }}
          >
            <X size={18} />
          </IconButton>

          {/* Icon + header */}
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: "1rem", pr: "3rem" }}>
            {/* Amber warning icon with ring */}
            <Box
              sx={{
                flexShrink: 0,
                width: "4rem",
                height: "4rem",
                borderRadius: "50%",
                bgcolor: "rgba(245, 158, 11, 0.1)",
                boxShadow: "0 0 0 0.5rem rgba(245, 158, 11, 0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  width: "3rem",
                  height: "3rem",
                  borderRadius: "50%",
                  bgcolor: "#F59E0B",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: Colors.WHITE,
                  boxShadow: "0 0.125rem 0.375rem rgba(0,0,0,0.15)",
                }}
              >
                <AlertTriangle size={20} strokeWidth={2.3} />
              </Box>
            </Box>

            {/* Title + message */}
            <Box sx={{ minWidth: 0, flex: 1, pt: "0.125rem" }}>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  px: "0.75rem",
                  py: "0.25rem",
                  mb: "0.625rem",
                  borderRadius: "999px",
                  border: `1px solid ${Colors.ACCENT_GRAY}`,
                  bgcolor: Colors.BACKGROUND_GRAY,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.6875rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    color: Colors.MAIN_GRAY,
                  }}
                >
                  {contextLabel}
                </Typography>
              </Box>

              <Typography
                sx={{
                  fontSize: "1.625rem",
                  fontWeight: 600,
                  lineHeight: 1.15,
                  color: Colors.TEXT_GRAY_DARK,
                }}
              >
                {title}
              </Typography>

              <Typography
                sx={{
                  mt: "0.75rem",
                  fontSize: "1rem",
                  lineHeight: 1.6,
                  color: "rgba(77, 77, 77, 0.78)",
                }}
              >
                {message}
              </Typography>
            </Box>
          </Box>

          {/* Action buttons */}
          <Box
            sx={{
              mt: "2rem",
              display: "flex",
              flexDirection: "column",
              gap: "0.75rem",
            }}
          >
            <CustomButton
              onClick={onKeepEditing}
              sx={{
                width: "100%",
                height: "3rem",
                fontSize: "1rem",
                fontWeight: 600,
                borderRadius: "1rem",
                background: Colors.PRIMARY,
                display: "flex",
                gap: "0.5rem",
                "&:hover": { background: Colors.PRIMARY_DARK },
              }}
            >
              <Pencil size={16} />
              {keepEditingText}
            </CustomButton>

            <CustomButton
              onClick={onDiscard}
              sx={{
                width: "100%",
                height: "3rem",
                fontSize: "1rem",
                fontWeight: 600,
                borderRadius: "1rem",
                background: Colors.WHITE,
                border: `1.5px solid ${Colors.ACCENT_GRAY}`,
                color: Colors.BROWN,
                display: "flex",
                gap: "0.5rem",
                "&:hover": {
                  background: Colors.BACKSHADE_GRAY,
                  border: `1.5px solid ${Colors.ACCENT_GRAY}`,
                },
              }}
            >
              <Trash2 size={16} />
              {discardText}
            </CustomButton>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default DiscardConfirmationModal;
